import { useEffect, useRef, useState } from "react";
import { TourStepIcon } from "./tour-icons";
import type { TourStep } from "./tour-content";
import { FintheonNumericText } from "../numbers/FintheonNumericText";

const CARD_WIDTH = 360;
const CARD_GAP = 16;
const VP_MARGIN = 12;

export function TourCard({
  step,
  stepIndex,
  totalSteps,
  targetRect,
  onNext,
  onPrev,
  onSkip,
}: {
  step: TourStep;
  stepIndex: number;
  totalSteps: number;
  targetRect: DOMRect | null;
  onNext: () => void;
  onPrev: () => void;
  onSkip: () => void;
}) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [cardHeight, setCardHeight] = useState(200);

  useEffect(() => {
    if (cardRef.current) setCardHeight(cardRef.current.offsetHeight);
  }, [step]);

  const position = getCardPosition({ step, targetRect, cardHeight });
  const isLast = stepIndex === totalSteps - 1;
  const progressLabel = `${stepIndex + 1} of ${totalSteps}`;

  return (
    <div
      ref={cardRef}
      className="fintheon-tour-card-shell fixed z-[10000]"
      style={{
        top: position.top,
        left: position.left,
        width: CARD_WIDTH,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <section
        className="fintheon-tour-card"
        data-tour-card={step.id}
        aria-labelledby={`fintheon-tour-title-${step.id}`}
      >
        <div className="fintheon-tour-card__header">
          <span className="fintheon-tour-card__icon">
            <TourStepIcon
              name={step.icon}
              className="fintheon-tour-card__icon-svg"
            />
          </span>
          <div className="min-w-0">
            <p className="fintheon-tour-card__kicker">{step.kicker}</p>
            <h3
              id={`fintheon-tour-title-${step.id}`}
              className="fintheon-tour-card__title"
            >
              {step.title}
            </h3>
          </div>
        </div>

        <div className="fintheon-tour-card__body">
          <p className="fintheon-tour-card__description">{step.description}</p>
          <p className="fintheon-tour-card__action">{step.action}</p>
        </div>

        <div className="fintheon-tour-card__footer">
          <span className="fintheon-tour-card__progress">
            <FintheonNumericText text={progressLabel} />
          </span>
          <div className="fintheon-tour-card__actions">
            {stepIndex > 0 && (
              <button
                type="button"
                onClick={onPrev}
                className="fintheon-tour-card__button fintheon-tour-card__button--secondary fintheon-secondary-action"
              >
                Back
              </button>
            )}
            <button
              type="button"
              onClick={onNext}
              className="fintheon-tour-card__button fintheon-tour-card__button--primary"
            >
              {isLast ? "Finish Tour" : step.primaryLabel}
            </button>
          </div>
        </div>

        <div className="fintheon-tour-card__skip-row">
          <button
            type="button"
            onClick={onSkip}
            className="fintheon-tour-card__skip"
          >
            Skip tour
          </button>
        </div>
      </section>
    </div>
  );
}

function getCardPosition({
  step,
  targetRect,
  cardHeight,
}: {
  step: TourStep;
  targetRect: DOMRect | null;
  cardHeight: number;
}) {
  let top = window.innerHeight / 2 - cardHeight / 2;
  let left = window.innerWidth / 2 - CARD_WIDTH / 2;

  if (targetRect) {
    if (step.position === "right") {
      left = targetRect.right + CARD_GAP;
      top = targetRect.top + targetRect.height / 2 - cardHeight / 2;
    }
    if (step.position === "left") {
      left = targetRect.left - CARD_WIDTH - CARD_GAP;
      top = targetRect.top + targetRect.height / 2 - cardHeight / 2;
    }
    if (step.position === "bottom") {
      left = targetRect.left + targetRect.width / 2 - CARD_WIDTH / 2;
      top = targetRect.bottom + CARD_GAP;
    }
    if (step.position === "top") {
      left = targetRect.left + targetRect.width / 2 - CARD_WIDTH / 2;
      top = targetRect.top - cardHeight - CARD_GAP;
    }
  }

  return {
    left: Math.max(
      VP_MARGIN,
      Math.min(left, window.innerWidth - CARD_WIDTH - VP_MARGIN),
    ),
    top: Math.max(
      VP_MARGIN,
      Math.min(top, window.innerHeight - cardHeight - VP_MARGIN),
    ),
  };
}
